import { useState, useRef, useCallback, useEffect } from "react";

interface RangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  step?: number;
  unit?: string;
  formatValue?: (value: number) => string;
  onChange: (min: number, max: number) => void;
}

export function RangeSlider({
  min,
  max,
  minValue,
  maxValue,
  step = 1,
  unit = "",
  formatValue,
  onChange,
}: RangeSliderProps) {
  const [isDragging, setIsDragging] = useState<"min" | "max" | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Format display value
  const displayValue = (value: number) => {
    if (formatValue) return formatValue(value);
    return `${value.toLocaleString()}${unit}`;
  };

  // Calculate percentage position
  const getPercent = (value: number) => {
    return ((value - min) / (max - min)) * 100;
  };

  // Convert position to value
  const positionToValue = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return min;
      const rect = trackRef.current.getBoundingClientRect();
      const percent = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      const rawValue = min + percent * (max - min);
      const steppedValue = Math.round(rawValue / step) * step;
      return Math.max(min, Math.min(max, steppedValue));
    },
    [min, max, step],
  );

  // Handle mouse/touch events
  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging) return;
      const newValue = positionToValue(clientX);

      if (isDragging === "min") {
        onChange(Math.min(newValue, maxValue - step), maxValue);
      } else {
        onChange(minValue, Math.max(newValue, minValue + step));
      }
    },
    [isDragging, positionToValue, minValue, maxValue, step, onChange],
  );

  // Mouse events
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleMouseUp = () => setIsDragging(null);

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMove]);

  // Touch events
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0 && e.touches[0]) {
        handleMove(e.touches[0].clientX);
      }
    };
    const handleTouchEnd = () => setIsDragging(null);

    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, handleMove]);

  // Handle track click to move nearest thumb
  const handleTrackClick = (e: React.MouseEvent) => {
    const clickValue = positionToValue(e.clientX);
    const distToMin = Math.abs(clickValue - minValue);
    const distToMax = Math.abs(clickValue - maxValue);

    if (distToMin <= distToMax) {
      onChange(Math.min(clickValue, maxValue - step), maxValue);
    } else {
      onChange(minValue, Math.max(clickValue, minValue + step));
    }
  };

  const minPercent = getPercent(minValue);
  const maxPercent = getPercent(maxValue);

  return (
    <div className="space-y-4">
      {/* Value display */}
      <div className="flex justify-between text-sm">
        <span className="text-gray-700 font-medium">
          {displayValue(minValue)}
        </span>
        <span className="text-gray-400">—</span>
        <span className="text-gray-700 font-medium">
          {displayValue(maxValue)}
        </span>
      </div>

      {/* Slider track wrapper - adds padding for thumb overflow */}
      <div className="px-2.5">
        <div
          ref={trackRef}
          className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
          onClick={handleTrackClick}
        >
          {/* Active range */}
          <div
            className="absolute h-full bg-red-600 rounded-full"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`,
            }}
          />

          {/* Min thumb */}
          <button
            type="button"
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-red-600 rounded-full shadow-md cursor-grab hover:scale-110 transition-transform ${
              isDragging === "min"
                ? "scale-110 cursor-grabbing ring-4 ring-red-100"
                : ""
            }`}
            style={{ left: `${minPercent}%`, marginLeft: "-10px" }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsDragging("min");
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDragging("min");
            }}
            aria-label="Minimum value"
          />

          {/* Max thumb */}
          <button
            type="button"
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-red-600 rounded-full shadow-md cursor-grab hover:scale-110 transition-transform ${
              isDragging === "max"
                ? "scale-110 cursor-grabbing ring-4 ring-red-100"
                : ""
            }`}
            style={{ left: `${maxPercent}%`, marginLeft: "-10px" }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsDragging("max");
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsDragging("max");
            }}
            aria-label="Maximum value"
          />
        </div>
      </div>
    </div>
  );
}
